import { format, max, min } from "date-fns";
import { cs, pl } from "date-fns/locale";
import { useTranslations } from "next-intl";
import { ListingItemImage } from "@/components/listing/listing-item-image";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { CartDietAddons } from "@/features/cart/components/cart/cart-diet-addons";
import { CartDietDeliveryAlert } from "@/features/cart/components/cart/cart-diet-delivery-alert";
import { CartDietFooter } from "@/features/cart/components/cart/cart-diet-footer";
import { CartDietHeader } from "@/features/cart/components/cart/cart-diet-header";
import { DietTags } from "@/features/diets/components/diet-tags";
import type {
  DietCatalog,
  DietCatalogDetail,
  UITag,
} from "@/features/diets/types";
import { getLocaleForPathname, getPathname } from "@/i18n/navigation";
import { getKcalFromMeals } from "@/lib/helpers/calorifics";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { useMobile } from "@/lib/hooks/use-mobile";
import {
  type FilterableParams,
  getConfiguratorParams,
  objectToQueryString,
} from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { CartItem } from "../../types";
import { getVariantFromMealsAndCalories } from "../../utils/variants";

export const CartDiet = ({
  diet,
  item,
  disabled,
}: {
  diet?: DietCatalog;
  item: CartItem;
  disabled?: boolean;
}) => {
  const t = useTranslations();

  const { isMobile } = useMobile();

  if (!diet) return null;
  const locale = getLocaleForPathname();

  const parseLocale = (locale: string) => {
    switch (locale) {
      case "cz":
        return cs;
      default:
        return pl;
    }
  };

  const dateLocale = parseLocale(locale);

  const configParams = getConfiguratorParams<FilterableParams>({
    configuration: diet.searchResultId || "",
  });

  const variantTag = diet.variants?.length
    ? {
        name: `${getVariantFromMealsAndCalories(diet, item.configurationItem.mealIds, item.configurationItem.calories)?.name}`,
        slug: "variant",
      }
    : {
        name: t("common.meals", {
          meals: item.configurationItem.mealIds.length,
        }),
        slug: "meals",
      };

  const tags: UITag[] = [
    {
      label: t("common.daysRangeSimple", {
        from: isMobile
          ? format(min(item.configurationItem.dates), "dd MMM", {
              locale: dateLocale,
            })
          : format(min(item.configurationItem.dates), "dd.MM.yyyy"),
        to: isMobile
          ? format(max(item.configurationItem.dates), "dd MMM", {
              locale: dateLocale,
            })
          : format(max(item.configurationItem.dates), "dd.MM.yyyy"),
      }),
      name: "days",
      type: "days",
      className: disabled ? "text-brand-red opacity-100" : "",
    },
    {
      label: t("common.kcal", {
        kcal:
          getKcalFromMeals(
            diet as DietCatalogDetail,
            item.configurationItem.calories,
            item.configurationItem.mealIds,
          ) || item.configurationItem.calories,
      }),
      name: "calories",
      type: "calories",
    },
    {
      label: variantTag.name,
      name: "variant",
      type: "meals",
    },
  ];

  return (
    <Card
      className={cn(
        "relative flex w-full flex-col rounded-lg border border-brand-grey2 bg-brand-white shadow-none",
        { "border-brand-red": disabled },
      )}
    >
      {disabled && (
        <div className="w-full p-4 pb-0">
          <CartDietDeliveryAlert item={item} />
        </div>
      )}
      <div className="flex w-full flex-col md:flex-row">
        <div className="relative md:flex md:py-6 md:pl-6">
          <ListingItemImage
            className="hidden overflow-hidden rounded-xl border border-brand-grey2 md:block md:size-30 md:rounded-xl"
            imageUrl={diet.imageUrl}
            name={formatDietName(diet.name)}
            url={
              getPathname({
                href: {
                  pathname: "/configure-diet/[slug]",
                  params: { slug: diet.slug },
                },
                locale,
              }) + objectToQueryString(configParams)
            }
          />
        </div>
        <CardContent className="flex flex-1 flex-col p-6 pl-4">
          <div className="flex gap-4">
            <div className="flex grow flex-col md:gap-1">
              <div className="mb-4 flex items-start border-brand-grey2 border-b pb-4 md:mb-0 md:justify-between md:border-0 md:pb-0">
                <ListingItemImage
                  className="mr-3 h-22 w-22 shrink-0 overflow-hidden rounded-lg md:hidden"
                  imageUrl={diet.imageUrl}
                  name={formatDietName(diet.name)}
                  url={
                    getPathname({
                      href: {
                        pathname: "/configure-diet/[slug]",
                        params: { slug: diet.slug },
                      },
                      locale,
                    }) + objectToQueryString(configParams)
                  }
                />
                <CartDietHeader diet={diet} item={item} />
              </div>
              <ScrollArea className="w-full overflow-x-auto">
                <div className="flex flex-nowrap gap-1.5">
                  <div className="flex flex-wrap gap-1 md:gap-2">
                    <DietTags tags={tags} />
                  </div>
                </div>
                <ScrollBar className="hidden" orientation="horizontal" />
              </ScrollArea>
            </div>
          </div>
          <CartDietFooter item={item} />
        </CardContent>
      </div>
      <CartDietAddons
        cateringName={
          diet.catering?.name
            ? formatCateringName(diet.catering.name)
            : undefined
        }
        dietId={diet.id}
        item={item}
      />
    </Card>
  );
};
